/**
 * G2: 分配ゲーム（Trust Game 系）
 * 相手キャラクタの「信頼度プロファイル」でゲームに変化をつける。
 * 10試行を通じてキャラを切り替え、低/中/高信頼帯の均一な暴露を保証する。
 * （単一キャラ固定だと裏切り遭遇回数が 0〜10 でばらつき learningSpeed が比較不能になる）
 */

import { computeTrustScore, type TrustTrialRecord } from '../scoring/trust.js';

const TOTAL_TRIALS = 10;
const MULTIPLIER = 3; // 委託額は3倍されて相手に届く

// 相手キャラクタの信頼度プロファイル（返金率の分布を定義）
// returnRate: 受け取り額に対して何割を返すか（0.0–1.0）
// variance: 試行ごとのばらつき幅
interface CharProfile {
  id: string;
  icon: string;
  name: string;
  desc: string;
  returnRate: number;
  variance: number;
}

const CHAR_PROFILES: CharProfile[] = [
  { id: 'fox',     icon: '🦊', name: 'キツネ',   desc: '抜け目がない',           returnRate: 0.10, variance: 0.05 },
  { id: 'cat',     icon: '🐱', name: 'ネコ',     desc: 'マイペース',             returnRate: 0.25, variance: 0.10 },
  { id: 'bear',    icon: '🐻', name: 'クマ',     desc: 'どっしりとしている',     returnRate: 0.35, variance: 0.08 },
  { id: 'rabbit',  icon: '🐰', name: 'ウサギ',   desc: '少し臆病',               returnRate: 0.40, variance: 0.15 },
  { id: 'dog',     icon: '🐶', name: 'イヌ',     desc: '忠実で正直',             returnRate: 0.50, variance: 0.08 },
  { id: 'panda',   icon: '🐼', name: 'パンダ',   desc: 'のんびりしている',       returnRate: 0.55, variance: 0.12 },
  { id: 'owl',     icon: '🦉', name: 'フクロウ', desc: '思慮深い',               returnRate: 0.60, variance: 0.06 },
  { id: 'koala',   icon: '🐨', name: 'コアラ',   desc: '穏やかで安定している',   returnRate: 0.65, variance: 0.05 },
  { id: 'deer',    icon: '🦌', name: 'シカ',     desc: 'おっとりと誠実',         returnRate: 0.70, variance: 0.07 },
  { id: 'penguin', icon: '🐧', name: 'ペンギン', desc: 'チームを大切にする',     returnRate: 0.80, variance: 0.06 },
];

/** Fisher-Yates シャッフル */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * 10試行分のキャラシーケンスを生成する。
 * 低信頼(returnRate≤0.35) 3体・中信頼 4体・高信頼(≥0.60) 3体をシャッフル。
 * これにより全被験者が一定数の裏切りイベントに遭遇し learningSpeed を比較可能にする。
 */
function buildCharSequence(): CharProfile[] {
  // low(≤0.35): fox/cat/bear=3, mid(0.36-0.64): rabbit/dog/panda/owl=4, high(≥0.65): koala/deer/penguin=3 → 計10
  const low  = CHAR_PROFILES.filter(c => c.returnRate <= 0.35);
  const mid  = CHAR_PROFILES.filter(c => c.returnRate > 0.35 && c.returnRate < 0.65);
  const high = CHAR_PROFILES.filter(c => c.returnRate >= 0.65);
  const picked = [
    ...shuffle(low).slice(0, 3),
    ...shuffle(mid).slice(0, 4),
    ...shuffle(high).slice(0, 3),
  ];
  return shuffle(picked);
}

/** 返金額を計算する（variance で揺らがせる） */
function calcReturn(invested: number, profile: CharProfile): number {
  const received = invested * MULTIPLIER;
  const noise = (Math.random() * 2 - 1) * profile.variance;
  const rate = Math.max(0, Math.min(1, profile.returnRate + noise));
  return Math.round(received * rate);
}

interface TrustState {
  trial: number;
  invested: number;
  chars: CharProfile[];
  records: TrustTrialRecord[];
  phase: 'invest' | 'result';
  lastReturn: number | null;
  lastInvested: number | null;
}

export function startTrustGame(container: HTMLElement, onComplete: (records: TrustTrialRecord[]) => void): void {
  const state: TrustState = {
    trial: 0,
    invested: 50,
    chars: buildCharSequence(),
    records: [],
    phase: 'invest',
    lastReturn: null,
    lastInvested: null,
  };

  function getHistoryText(): string {
    if (state.records.length === 0) return '（まだ試行なし）';
    const last = state.records[state.records.length - 1];
    const gained = last.returned - last.invested;
    const sign = gained >= 0 ? '+' : '';
    return `前回: ${last.invested} pt 委託 → ${last.returned} pt 返金 （差: ${sign}${gained} pt）`;
  }

  function render(): void {
    const progressPct = (state.trial / TOTAL_TRIALS) * 100;

    let resultMsg = '';
    if (state.lastReturn !== null && state.lastInvested !== null) {
      const gained = state.lastReturn - state.lastInvested;
      if (gained < 0) {
        resultMsg = `<div class="trust-result-msg betrayed">前回は裏切られました（${gained} pt）</div>`;
      } else {
        resultMsg = `<div class="trust-result-msg returned">前回は返金されました（+${gained} pt）</div>`;
      }
    }

    container.innerHTML = `
      <div class="trial-counter">試行 ${state.trial + 1} / ${TOTAL_TRIALS}</div>
      <div class="progress-bar-wrap">
        <div class="progress-bar-fill" style="width: ${progressPct}%"></div>
      </div>
      <div class="trust-arena">
        <div class="trust-char">
          <div class="trust-char-icon">${state.chars[state.trial].icon}</div>
          <div class="trust-char-name">${state.chars[state.trial].name}</div>
          <div class="trust-char-desc">${state.chars[state.trial].desc}</div>
        </div>
        <div class="trust-history">
          <div class="trust-history-label">前回の結果</div>
          ${getHistoryText()}
        </div>
        ${resultMsg}
        <div class="trust-amount-display">
          <div class="trust-amount-number" id="invest-display">${state.invested}</div>
          <div class="trust-amount-label">pt を委託する（所持: 100 pt）</div>
        </div>
        <div class="trust-slider-wrap">
          <input type="range" class="trust-slider" id="invest-slider"
            min="0" max="100" value="${state.invested}" step="5" />
          <div class="trust-slider-labels"><span>0</span><span>50</span><span>100</span></div>
        </div>
        <button class="btn btn-primary" id="btn-invest">この金額を委託する</button>
      </div>
    `;

    const slider = document.getElementById('invest-slider') as HTMLInputElement;
    const display = document.getElementById('invest-display')!;
    slider.addEventListener('input', () => {
      state.invested = Number(slider.value);
      display.textContent = String(state.invested);
    });

    document.getElementById('btn-invest')!.addEventListener('click', handleInvest);
  }

  function handleInvest(): void {
    if (state.phase !== 'invest') return;
    state.phase = 'result';

    const invested = state.invested;
    const returned = calcReturn(invested, state.chars[state.trial]);

    state.records.push({
      trial: state.trial,
      invested,
      returned,
      charId: state.chars[state.trial].id,
    });

    state.lastInvested = invested;
    state.lastReturn = returned;

    // 結果を一時表示してから次の試行へ
    showReturnResult(invested, returned, () => {
      state.trial += 1;
      if (state.trial >= TOTAL_TRIALS) {
        onComplete(state.records);
        return;
      }
      state.phase = 'invest';
      render();
    });
  }

  function showReturnResult(invested: number, returned: number, next: () => void): void {
    const gained = returned - invested;
    const sign = gained >= 0 ? '+' : '';
    const isBetrayed = gained < 0;

    container.innerHTML = `
      <div class="trust-arena" style="justify-content:center; align-items:center; text-align:center; gap:16px;">
        <div class="trust-char-icon" style="font-size:4rem;">${state.chars[state.trial].icon}</div>
        <div style="font-size:1.1rem; font-weight:700;">${state.chars[state.trial].name} の回答</div>
        <div style="font-size:0.9rem; color:#555;">
          委託: ${invested} pt → 相手受取: ${invested * MULTIPLIER} pt
        </div>
        <div style="font-size:2rem; font-weight:700; color:${isBetrayed ? '#ff6b6b' : '#2ecc71'}">
          返金: ${returned} pt
        </div>
        <div style="font-size:1.1rem; color:${isBetrayed ? '#ff6b6b' : '#2ecc71'}; font-weight:600;">
          差引 ${sign}${gained} pt
        </div>
      </div>
    `;

    setTimeout(next, 1800);
  }

  render();
}

export { computeTrustScore };
